import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  WritableSignal,
  computed,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, Subject, combineLatest, interval, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { CurrencyWalletService } from 'src/app/layouts/currency-wallet/currency-wallet.service';
import { GameApi } from 'src/app/shared/apis/game.api';
import { GameOrderApi } from 'src/app/shared/apis/gameorder.api';
import { ImgCarouselOptions } from 'src/app/shared/components/img-carousel/img-carousel-options.interface';
import {
  HeaderMenu,
  HomeLabelSceneData,
  ScenesGameResponse,
  ScenesInfo,
} from 'src/app/shared/interfaces/game.interface';
import { LatestWinnerItem, LatestWinnerParams } from 'src/app/shared/interfaces/gameorder.interface';
import { SportEvent } from 'src/app/shared/interfaces/sport-event.interface';
import { CollectorService } from 'src/app/shared/service/collector.service';
import { DataCollectionService } from 'src/app/shared/service/data-collection.service';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from '../../shared/service/localstorage.service';
import { DailyRacesService } from '../daily-races/daily-races.service';
import { LoginService } from '../login-dialog/login.service';
import { MiniGameService } from '../minigame/minigame.service';
import { WalletService } from './../wallet/wallet.service';

@UntilDestroy()
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, OnDestroy {
  /** H5 判斷（沿用你的變數, 但來源改成狀態流） */
  isH5!: boolean;

  constructor(
    public localStorageService: LocalStorageService,
    public appService: AppService,
    public collectorService: CollectorService,
    private router: Router,
    private layout: LayoutService,
    private miniGameService: MiniGameService,
    private dataCollectionService: DataCollectionService,
    private gameApi: GameApi,
    public loginService: LoginService,
    public walletService: WalletService,
    private route: ActivatedRoute,
    private gameOrderApi: GameOrderApi,
    private currencyWalletService: CurrencyWalletService,
    private toast: ToastService,
    private dailyRacesService: DailyRacesService,
    private cdr: ChangeDetectorRef,
  ) {}

  // ================= UI Config =================
  sportData: SportEvent[] = [];
  imgCarouselConfig: ImgCarouselOptions = {
    slidesPerView: 4,
    slidesPerGroup: 4,
    spaceBetween: 10,
    speed: 500,
    breakpoints: {
      1400: { slidesPerView: 3, slidesPerGroup: 3 },
      800: { slidesPerView: 1, slidesPerGroup: 1 },
    },
  };

  /** 注意：若 ImgCarouselOptions 型別是固定鍵名，保持你原本的 `deplay` 以免破壞相容 */
  latestWinnerImgCarouselConfig: ImgCarouselOptions = {
    slidesPerView: 14,
    slidesPerGroup: 1,
    spaceBetween: 8,
    speed: 300,
    deplay: 2000,
    loop: true,
    allowTouchMove: true,
    autoplay: true,
    breakpoints: {
      850: { slidesPerView: 8.5, slidesPerGroup: 1, spaceBetween: 8 },
      500: { slidesPerView: 4.5, slidesPerGroup: 1, spaceBetween: 8 },
    },
  };

  periodYears: string = '';

  winnerList: LatestWinnerItem[] = [];
  winnerLoading: boolean = false;

  private winnerApiList: LatestWinnerItem[] = [];
  private readonly winnerPageSize = 32;
  isFiatDisplay: boolean = false;

  /** casino跟Sports入口 */
  entranceList = [
    {
      title: 'casino',
      content: 'home_entrance_casino_desc',
      imgUrl: 'assets/images/home/entrance/casino.jpeg',
      route: 'casino/home/index',
      alt: 'casino_lobby_entrance_artwork',
      closeTxt: '',
    },
    {
      title: 'sport',
      content: 'home_entrance_sport_desc',
      imgUrl: 'assets/images/home/entrance/sports.jpeg',
      route: 'play/SoftswissSport-1/All',
      alt: 'sportsbook_entrance_artwork',
      closeTxt: '',
    },
    // {
    //   title: 'Live Casino',
    //   content: 'Experience the thrill of live play',
    //   imgUrl: 'assets/images/home/entrance/sports.png',
    //   route: '',
    //   alt: 'LiveCasino entrance artwork',
    // },
  ];

  // ================= Lifecycle =================
  ngOnInit() {
    this.currencyWalletService.displayInFiat$.subscribe(x => {
      this.isFiatDisplay = x;
    });
    this.periodYears = (new Date().getFullYear() - 2006).toString();

    // isH5 狀態
    this.layout.isH5$.pipe(untilDestroyed(this)).subscribe(e => (this.isH5 = e));

    this.setPoint('in');

    // 初始化各模組
    this.bindHomeScenes(); // 真人/小遊戲板塊
    this.bindLatestWinner(); // 最新得獎名單
    // pubs不用熱門賽事
    // this.bindHotSports(); // 熱門賽事

    // 進出頁事件
    this.layout
      .watchPageReuse('')
      .pipe(untilDestroyed(this))
      .subscribe(x => {
        switch (x.type) {
          case 'enter':
            this.setPoint('in');
            break;
          case 'leave':
            this.setPoint('out');
            break;
        }
      });
  }

  ngOnDestroy(): void {
    this.setPoint('out');
  }

  // ================== 首頁板塊（真人/小遊戲） ==================

  /** 用 signal 維持你的模板相容性 */
  homeScenesInfo: WritableSignal<ScenesInfo[]> = signal([]);
  renderHomeScenesInfo = computed(() => {
    if (this.homeScenesInfo().length > 0) return this.homeScenesInfo();
    return Array(7).fill(null);
  });

  loadingGameList = true;

  /** 將原本的 initHomeScenes 改成穩定的資料流，避免 setTimeout/競態 */
  private bindHomeScenes() {
    this.loadingGameList = true;

    // 來源：miniGameService.homeScenesSub$（HomeLabelSceneData）
    const homeSceneRaw$ = this.miniGameService.homeScenesSub$.pipe(
      filter(Boolean),
      tap((x: HomeLabelSceneData) => (this.miniGameService.allScenesData = x)),
      map(x => x!.homeScene as HeaderMenu),
      shareReplay(1),
    );

    // 取出 labelIds 後打 API
    const sceneWithGames$ = homeSceneRaw$.pipe(
      switchMap(menu => {
        const ids: number[] = menu?.infoVerticallyList?.map((i: any) => i?.labelId).filter(Boolean) ?? [];
        if (!ids.length) return of<ScenesGameResponse[]>([]);
        return this.miniGameService.getGameListByIds(ids).pipe(catchError(() => of<ScenesGameResponse[]>([])));
      }),
      withLatestFrom(homeSceneRaw$),
      map(([resp, menu]) => {
        const list = menu?.infoVerticallyList ?? [];
        const gameGroups = list.map((element: any, index: number) => ({
          ...element,
          gameLists: resp[index]?.gameList,
          count: resp[index]?.count || 0,
          titleHref: `/${this.appService.languageCode}${this.miniGameService.getLinkByMethod(element)}`,
          redirectMethod: element?.redirectMethod,
        }));
        return gameGroups as ScenesInfo[];
      }),
      shareReplay(1),
    );

    // 寫回 signal
    sceneWithGames$.pipe(untilDestroyed(this)).subscribe(groups => {
      this.loadingGameList = false;
      this.homeScenesInfo.set(groups ?? []);
    });
  }

  trackByFn(index: number) {
    return index;
  }

  // ================== 熱門賽事（含倒數/正數時計） ==================

  private hotSportsBase$ = new BehaviorSubject<Array<SportEvent & { fetchedAt?: number; baseGameTime?: number }>>([]);
  isHotSport = true;

  /** timer poll + 以 elapsed 計算顯示（避免每秒覆蓋來源造成跳秒/回彈） */
  private bindHotSports() {
    // 每 30 秒拉一次資料；抓到時記錄 fetchedAt 與 baseGameTime
    const fetch$ = timer(0, 30000).pipe(
      switchMap(() => this.gameApi.getHotMatch(this.appService.languageCode, 10)),
      catchError(() => of([] as SportEvent[])),
      map(list =>
        (list ?? []).map(item => ({
          ...item,
          fetchedAt: Date.now(),
          baseGameTime: item.gameTime ?? -1,
        })),
      ),
      tap(list => {
        this.isHotSport = list.length > 0;
        this.hotSportsBase$.next(list);
      }),
      shareReplay(1),
    );

    // 每秒依 elapsed 推導顯示值（不突變 base 狀態）
    const ticking$ = interval(1000).pipe(startWith(0));

    combineLatest([this.hotSportsBase$, ticking$])
      .pipe(
        map(([list]) =>
          list.map(base => {
            const fetchedAt = base.fetchedAt ?? Date.now();
            const start = base.baseGameTime ?? -1;

            if (start == null || start === -1) {
              return { ...base, gameTime: start, remainingTime: '00:00' };
            }

            const elapsed = Math.floor((Date.now() - fetchedAt) / 1000);
            const t = base.sportId === 1 ? start + elapsed : Math.max(0, start - elapsed);
            const mm = Math.floor(t / 60);
            const ss = t % 60;
            return {
              ...base,
              gameTime: t,
              remainingTime: `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`,
            };
          }),
        ),
        untilDestroyed(this),
      )
      .subscribe(renderList => {
        this.sportData = renderList;
      });

    fetch$.pipe(untilDestroyed(this)).subscribe();
  }

  // ================== 最新得獎名單 ==================

  private bindLatestWinner() {
    this.winnerLoading = true;

    const params: LatestWinnerParams = {
      TenantId: environment.common.tenant,
      PageIndex: 1,
      PageSize: this.winnerPageSize,
    };

    this.dailyRacesService.homeWinnerList$.pipe(untilDestroyed(this)).subscribe(pushed => {
      this.winnerList = this.mergeWinners(this.winnerApiList, pushed);
      this.cdr.markForCheck();
    });

    timer(0, 1 * 60 * 1000)
      .pipe(
        switchMap(() => this.gameOrderApi.getLatestWinner(params)),
        catchError(() => of([] as LatestWinnerItem[])),
        untilDestroyed(this),
      )
      .subscribe(list => {
        this.winnerLoading = false;
        this.winnerApiList = (list ?? []).slice();
        this.winnerList = this.mergeWinners(this.winnerApiList, this.dailyRacesService.homeWinnerList());
        this.cdr.markForCheck();
      });
  }

  /**
   * 推送資料置前，API 資料補足
   * @param api
   * @param pushed
   */
  private mergeWinners(api: LatestWinnerItem[], pushed: LatestWinnerItem[]): LatestWinnerItem[] {
    return [...pushed, ...api].slice(0, this.winnerPageSize);
  }

  // ================== 事件處理（導航/統計） ==================

  // 點擊遊戲
  clickGameItem(item: any) {
    if (item?.webRedirectUrl) {
      this.router.navigateByUrl(`${this.appService.languageCode}/${item.webRedirectUrl}`);
    } else {
      this.router.navigateByUrl(`${this.appService.languageCode}/casino/games/${item.providerCatId}/${item.gameId}`);
    }
  }

  // 點擊標題
  clickLabelTitle(item: ScenesInfo) {
    const url = `/${this.appService.languageCode}${this.miniGameService.getLinkByMethod(item)}`;
    if (url) this.router.navigateByUrl(url);
  }

  // 普通跳轉
  jumpTo(path: string, closeTxt: string = '') {
    if (closeTxt) {
      return;
    }
    this.router.navigateByUrl(`${this.appService.languageCode}/${path}`);
  }

  // QR Pop 原邏輯保留（僅小幅整理）
  qrPopPositions: ConnectedPosition[] = [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      panelClass: 'app-download-qr-pop-down',
    },
    { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', panelClass: 'app-download-qr-pop-up' },
  ];
  showPop$ = new Subject<boolean>();
  downQR = false;

  showPop(skipRepeat: boolean = true) {
    if (skipRepeat && this.downQR) return;
    this.closePop();
    this.showPop$.next(true);
    this.downQR = true;
  }

  closePop(time: number = 0) {
    if (time > 0) {
      timer(time)
        .pipe(takeUntil(this.showPop$), untilDestroyed(this))
        .subscribe(() => (this.downQR = false));
    } else {
      this.downQR = false;
    }
  }

  // 埋點
  private setPoint(type: 'in' | 'out') {
    switch (type) {
      case 'in':
        this.dataCollectionService.addPoint({ eventId: 30001 });
        this.dataCollectionService.setEnterTime('home');
        break;
      case 'out':
        this.dataCollectionService.addPoint({
          eventId: 30002,
          actionValue1: this.dataCollectionService.getTimDiff('home'),
          actionValue2: 0,
        });
        break;
    }
  }
}
